import cloneDeep from 'lodash.clonedeep';
import {
  ActiveFilter,
  filters,
  hostTypes,
} from 'modules/locals/components/LocalSearchFilters/guidesFilterData';
import {
  addFilterOptionInPlace,
  getLocalSearchBasePath,
} from 'modules/locals/utils/LocalSearchFilterHelper';
import LocalSearchUrlCompiler from 'modules/locals/utils/LocalSearchUrlCompiler';
import slugify from 'utils/slugify';

export const DEFAULT_HOSTS_PER_PAGE = 18;

export type FilterObject = {
  activeFilters: ActiveFilter[];
  country?: string;
  city?: string;
  hostType?: string;
  page?: number;
  hostsPerPage?: number;
  redirectUrl?: string;
  otherQueryParams?: URLSearchParams;
};

const localSearchBasePaths = ['/l/', '/locals/'];

const parse = (url: string, locations): FilterObject => {
  const result: FilterObject = {
    country: '',
    city: '',
    hostType: 'guides',
    activeFilters: [],
    page: 1,
    hostsPerPage: DEFAULT_HOSTS_PER_PAGE,
    redirectUrl: '',
    otherQueryParams: new URLSearchParams(),
  };

  const [fullPathname, queryValue] = url.split('?');
  const pathname = fullPathname
    .split(getLocalSearchBasePath(fullPathname))
    .pop();

  // to check later on against the expected url
  const strippedUrl = `${pathname[0] === '/' ? pathname : `/${pathname}`}${
    queryValue ? `?${queryValue}` : ''
  }`;

  // path
  if (!pathname) {
    result.country = '';
    result.city = '';
  } else {
    const pathParts = pathname
      .split('/')
      .filter((pathPart) => !localSearchBasePaths.includes(pathPart));
    const countryLocation = locations.find(({ country }) =>
      pathParts.includes(slugify(country, false)),
    );
    const cityLocation = countryLocation
      ? locations.find(({ country, city }) => {
          const countryIndex = pathParts.indexOf(slugify(country, false));
          const cityIndex = pathParts.lastIndexOf(slugify(city, false));
          return (
            countryIndex > -1 && cityIndex > -1 && cityIndex !== countryIndex
          );
        })
      : undefined;

    const country = countryLocation ? countryLocation.country : undefined;
    const city = cityLocation ? cityLocation.city : undefined;

    result.country = country;
    result.city = city;

    const hostType =
      hostTypes.find((hostType) => pathParts.includes(hostType)) || 'guides';
    result.hostType = hostType;

    filters.forEach((filter) => {
      const foundFilter = cloneDeep(
        filter.filterOptions.find((filterOption) =>
          pathParts.includes(filterOption.value),
        ),
      );
      if (!!foundFilter) {
        addFilterOptionInPlace({
          activeFilters: result.activeFilters,
          uid: filter.uid,
          filterOption: foundFilter,
        });
      }
    });
  }

  // search params
  const searchParams = queryValue
    ? new URLSearchParams(queryValue.toLowerCase())
    : new URLSearchParams();
  searchParams.forEach((value, key) => {
    const isValidFilter = cloneDeep(filters.find((f) => f.uid === key));
    if (isValidFilter) {
      const filterOptionToAdd =
        key === 'date.from' || key === 'date.to'
          ? {
              value,
              id: 1,
              name: key,
            }
          : isValidFilter.filterOptions.find(
              (filter) => filter.value === value,
            );
      addFilterOptionInPlace({
        activeFilters: result.activeFilters,
        uid: isValidFilter.uid,
        filterOption: filterOptionToAdd,
      });
    } else {
      // unknown query params
      result.otherQueryParams.append(key, value);
    }
  });

  const expectedUrl = LocalSearchUrlCompiler.compile(result);

  if (expectedUrl !== strippedUrl) {
    result.redirectUrl = expectedUrl;
  }

  return result;
};

export default {
  parse,
};
